import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../../api/client';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { unwrapArray } from '../../lib/api-envelope';
import { daysUntilCalendarDate, formatCalendarDate } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { parseMoney } from '../../lib/display-labels';
import type { ApiResponse } from '../../types/api-manifest';

/**
 * The Fleet units register: `trucking_fleet`, as GET /trucking/fleet sends it
 * (`f.*` plus the assigned driver's `driver_first_name` / `driver_last_name`).
 *
 * WHAT THIS PAGE USED TO SHOW THAT NOTHING SENDS, AND IS GONE:
 * - Odometer, engine hours and "km since service", hard-coded 0, under meter
 *   bars, against a hard-coded 8,000 km service interval. No column holds any
 *   of them.
 * - "Max payload", hard-coded 0 kg.
 * - "Tare weight", which was `gvw_kg`. GVW is the GROSS vehicle weight, the
 *   opposite end of the scale from tare; it is shown here under its own name.
 * - Upcoming Maintenance, Service Log and Regulatory Documents, each always
 *   empty and each saying "not available yet". No endpoint returns them. The
 *   one regulatory fact the table does hold, `plate_expiry`, is shown.
 * - Dispatched and Down statuses, and an unknown status falling back to
 *   Available. See STATUS.
 */

export interface FleetUnitRow {
  id: string;
  unit_number: string | null;
  type: string | null;
  year: number | null;
  make: string | null;
  model: string | null;
  vin: string | null;
  plate: string | null;
  plate_expiry: string | null;
  jurisdiction: string | null;
  status: string | null;
  driver: string | null;
  /** `trucking_fleet.assigned_driver_id`, a `trucking_drivers` id. */
  driver_id: string | null;
  gvw_kg: number | null;
  last_service_date: string | null;
  next_service_date: string | null;
  notes: string | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v));

function toUnit(r: Record<string, unknown>): FleetUnitRow {
  const year = parseMoney(r.year);
  return {
    id: String(r.id),
    unit_number: str(r.unit_number),
    type: str(r.type),
    year: year == null ? null : Math.trunc(year),
    make: str(r.make),
    model: str(r.model),
    vin: str(r.vin),
    plate: str(r.plate),
    plate_expiry: str(r.plate_expiry),
    jurisdiction: str(r.jurisdiction),
    status: str(r.status),
    driver: [str(r.driver_first_name), str(r.driver_last_name)].filter(Boolean).join(' ') || null,
    driver_id: str(r.assigned_driver_id),
    gvw_kg: parseMoney(r.gvw_kg),
    last_service_date: str(r.last_service_date),
    next_service_date: str(r.next_service_date),
    notes: str(r.notes),
  };
}

/**
 * The fleet list, shared by the register and the page's band so both read one
 * request. Retired units are excluded unless asked for, as the route does.
 * `type` narrows it the API's way (api#1045): `trailer` is type "trailer",
 * `truck` is every other unit. Omitted, every unit is listed.
 */
export function useFleetUnits(includeRetired: boolean, type?: 'truck' | 'trailer') {
  const params = new URLSearchParams();
  if (includeRetired) params.set('include_retired', 'true');
  if (type) params.set('type', type);
  const qs = params.toString();
  return useQuery({
    queryKey: ['trucking-fleet', includeRetired, type ?? 'all'],
    queryFn: () =>
      apiClient
        .get<ApiResponse<'GET /api/v1/trucking/fleet'>>(`/trucking/fleet${qs ? `?${qs}` : ''}`)
        .then((r) => unwrapArray<Record<string, unknown>>(r.data).map(toUnit)),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

type Skin = { label: string; fill: string; rail: string };

/**
 * `trucking_fleet.status` is free text. The API writes four values: `active`
 * (the default, and what reactivate sets), `maintenance` (named in the import
 * prompt), `retired` (DELETE sets it), and `out_of_service` (api#855: a trip
 * inspection's major defect sets it, and only the office certifying the repair
 * clears it — dispatch refuses the unit meanwhile). Anything else is a slate
 * dash with no rail: the old page showed it as Available, which on a fleet
 * register means "free to dispatch".
 *
 * Out of service takes red: of the five hues it is the one that means "do not
 * send", and nothing else on this register uses it.
 */
const STATUS: Record<string, Skin> = {
  active: { label: 'Active', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  maintenance: { label: 'Maintenance', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  retired: { label: 'Retired', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  out_of_service: { label: 'Out of Service', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
const UNKNOWN: Skin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };
export const fleetStatusSkin = (s: string | null) =>
  s != null && Object.prototype.hasOwnProperty.call(STATUS, s) ? STATUS[s] : UNKNOWN;

/**
 * A unit is due when its next service day is today or past — the TENANT's
 * today (companies.time_zone), not the device's. No date is not "due", and
 * neither is an unknown zone: nothing is known about today.
 */
export const isServiceDue = (u: FleetUnitRow, timeZone: string | null) => {
  const days = daysUntilCalendarDate(u.next_service_date, timeZone);
  return days != null && days <= 0;
};

export function StatusBadge({ status }: { status: string | null }) {
  const skin = fleetStatusSkin(status);
  return (
    <span
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none tracking-[0.06em] text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

export function DueBadge() {
  return (
    <span className="ml-2 inline-flex items-center rounded-fi-pill border border-black/20 bg-fi-signal-red px-2 py-0.5 text-[0.62rem] font-black uppercase leading-none text-fi-signal-ink">
      Due
    </span>
  );
}

export const fmtDay = (d: string | null) => formatCalendarDate(d, { year: 'numeric', month: 'short', day: 'numeric' }) ?? '—';
export const vehicleOf = (u: FleetUnitRow) => [u.year, u.make, u.model].filter((x) => x != null && x !== '').join(' ') || '—';

type FilterKey = 'all' | 'active' | 'maintenance' | 'out_of_service' | 'due';
/**
 * Chip counts are tallies of the loaded list. GET /trucking/fleet is
 * unpaginated (no LIMIT) and no endpoint returns these figures.
 */
const FILTERS: { key: FilterKey; label: string; match: (u: FleetUnitRow, timeZone: string | null) => boolean }[] = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'active', label: 'Active', match: (u) => u.status === 'active' },
  { key: 'maintenance', label: 'Maintenance', match: (u) => u.status === 'maintenance' },
  { key: 'out_of_service', label: 'Out of service', match: (u) => u.status === 'out_of_service' },
  { key: 'due', label: 'Service due', match: isServiceDue },
];

export default function FleetRegistryScreen() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  const [showRetired, setShowRetired] = useState(false);
  const timeZone = useTenantTimeZone();

  const fleetQuery = useFleetUnits(showRetired);
  const failed = queryLoadFailure(fleetQuery) != null;
  const loading = fleetQuery.isLoading && !failed;
  const fleet = failed ? undefined : fleetQuery.data;

  const countOf = (k: FilterKey): number | null => {
    const f = FILTERS.find((x) => x.key === k)!;
    return fleet ? fleet.filter((u) => f.match(u, timeZone)).length : null;
  };

  const filtered = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter)!;
    let rows = (fleet ?? []).filter((u) => f.match(u, timeZone));
    if (search) {
      const s = search.toLowerCase();
      rows = rows.filter((u) =>
        [u.unit_number, u.make, u.model, u.type, u.plate, u.vin, u.driver].some((v) => (v ?? '').toLowerCase().includes(s)),
      );
    }
    return rows;
  }, [fleet, filter, search, timeZone]);

  // A row opens the unit's own page (#815: registers, not split panes).
  const open = (id: string) => navigate(`/trucking/fleet/${encodeURIComponent(id)}`);

  const rowKeys = (id: string) => (event: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };

  return (
    <div data-testid="fleet-units" className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            const n = countOf(f.key);
            return (
              <button
                key={f.key}
                type="button"
                aria-pressed={active}
                onClick={() => setFilter(f.key)}
                className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                  active ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                }`}
              >
                {f.label}
                {!loading && n != null && <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{n}</span>}
              </button>
            );
          })}
          <button
            type="button"
            aria-pressed={showRetired}
            onClick={() => setShowRetired(!showRetired)}
            className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-fi-pill border px-4 py-1.5 text-xs font-extrabold transition ${
              showRetired ? 'border-fieldiq-text bg-fieldiq-card text-fieldiq-text' : 'border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
            }`}
          >
            Show retired
          </button>
        </div>
        <div className="relative lg:w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
          <input
            placeholder="Search unit, plate, VIN, driver..."
            aria-label="Search fleet units"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
          />
        </div>
      </div>

      <section
        aria-labelledby="fleet-register-title"
        className="min-w-0 overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
      >
        <header className="border-b border-fieldiq-border px-4 py-4">
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Fleet register</p>
          <h2 id="fleet-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Units</h2>
        </header>

        {failed ? (
          <LoadFailureNotice error={queryFailureError(fleetQuery)} subject="the fleet" />
        ) : loading ? (
          <div className="space-y-2 p-4" aria-hidden="true">
            {[1, 2, 3].map((i) => <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />)}
          </div>
        ) : filtered.length === 0 ? (
          <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">
            {(fleet ?? []).length === 0 ? 'No fleet units yet' : 'No units match these filters'}
          </p>
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full min-w-[820px] text-left">
                <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                  <tr className="border-l-4 border-l-transparent text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                    <th className="px-4 py-3">Unit</th>
                    <th className="px-4 py-3">Vehicle</th>
                    <th className="px-4 py-3">Plate</th>
                    <th className="px-4 py-3">Driver</th>
                    <th className="px-4 py-3">Next service</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr
                      key={u.id}
                      data-testid="fleet-row"
                      role="link"
                      tabIndex={0}
                      onClick={() => open(u.id)}
                      onKeyDown={rowKeys(u.id)}
                      className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition-colors hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${fleetStatusSkin(u.status).rail}`}
                    >
                      <td className="px-4 py-3">
                        <p className="font-mono text-sm font-extrabold text-fieldiq-text">{u.unit_number ?? '—'}</p>
                        <p className="text-[11px] text-fieldiq-muted">{u.type ?? '—'}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-fieldiq-text">{vehicleOf(u)}</td>
                      <td className="px-4 py-3 text-sm text-fieldiq-text">{u.plate ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-fieldiq-text">{u.driver ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-fieldiq-muted">
                        {fmtDay(u.next_service_date)}
                        {isServiceDue(u, timeZone) && <DueBadge />}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={u.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="space-y-3 p-4 md:hidden">
              {filtered.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  data-testid="fleet-card"
                  onClick={() => open(u.id)}
                  className="block w-full rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4 text-left"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-mono font-extrabold text-fieldiq-text">{u.unit_number ?? '—'}</p>
                      <p className="truncate text-xs text-fieldiq-muted">{vehicleOf(u)}</p>
                    </div>
                    <StatusBadge status={u.status} />
                  </div>
                  <p className="mt-2 text-xs text-fieldiq-muted">
                    Next service {fmtDay(u.next_service_date)}
                    {isServiceDue(u, timeZone) && <DueBadge />}
                  </p>
                </button>
              ))}
            </div>
            <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
              Showing {filtered.length} of {(fleet ?? []).length} units{showRetired ? ', retired included' : ''}
            </p>
          </>
        )}
      </section>
    </div>
  );
}
